import { Box, CardActions, CardContent, IconButton, Tooltip, Typography } from "@mui/material";
import { DownloadRounded, ErrorOutlineRounded } from "@mui/icons-material";
import { ExcelUploadedList } from "../../model/excel_upload_model";
import dayjs from "dayjs";
import Loading from "../../../../components/loading/loading";
import { black, greenLight, greyEnabled, orange, purpleLight, red, redLight } from "../../../../theme/colors";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../../../users/components/table_toolkit/table_toolkit";

const ROWS_PER_PAGE = 8;

interface DataGridInterface {
  label: { label: string }[];
  data: ExcelUploadedList[];
  count: number;
  page: number;
  setPage: (val: number) => void;
  isLoading?: boolean;
  isError?: boolean;
  downloadExcel: (val: number) => void
}

function statusColor(status: string) {
  if (status === "Failed") return red;
  if (status === "Completed") return "#1E6446";
  return orange;
}

export default function DatGridJFileUpload({
  label,
  data,
  page,
  setPage,
  count,
  isLoading = false,
  isError = false,
  downloadExcel
}: DataGridInterface) {
  const startRange = (page - 1) * ROWS_PER_PAGE + 1;
  const endRange = Math.min(page * ROWS_PER_PAGE, count);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1);
  };

  const customText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;
  const showList = !isLoading && !isError && data && data.length > 0;
  const showEmpty = !isLoading && !isError && (!data || data.length === 0);

  return (
    <Box display="flex" flex={1} flexDirection={"column"} width={"100%"}>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <Loading />
          </Box>
        ) : isError ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            py={8}
            gap={0.5}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 44,
                height: 44,
                borderRadius: "50%",
                bgcolor: redLight,
                mb: 1,
              }}
            >
              <ErrorOutlineRounded sx={{ color: red, fontSize: 24 }} />
            </Box>
            <Typography fontWeight={600} color={black}>
              Something went wrong
            </Typography>
            <Typography variant="body2" color={greyEnabled}>
              We couldn't load the uploaded files. Please try again.
            </Typography>
          </Box>
        ) : showEmpty ? (
          <TableEmptyState
            message="No files uploaded yet"
            hint="Try adjusting your filters, or upload a new file."
          />
        ) : (
          <Box mt={1}>
            <TableHeaderRow labels={label} lastColumnWidth={90} align="left" />
            {data?.map?.((item, index) => {
              const formattedDate = dayjs(item?.uploadedAt)?.format?.("DD MMM YYYY");
              let details: any = item?.remark?.details;
              let message: string = '';
              let value: string = '';
              if (details !== undefined) {
                try {
                  const parsed = JSON.parse(details);
                  message = parsed?.[0]?.message;
                  value = parsed?.[0]?.value;
                  if (parsed?.length === 0) {
                    message = item?.remark?.message;
                  }
                } catch {
                  message = item?.remark?.message;
                }
              }
              return (
                <TableRow key={item?.id ?? index}>
                  <TableCell label="Sl. No." flex={0.5}>
                    <Typography fontSize={14} noWrap>{(page - 1) * ROWS_PER_PAGE + index + 1}</Typography>
                  </TableCell>
                  <TableCell label="Document Name">
                    <Typography fontSize={14} noWrap>{item?.documentName || "-"}</Typography>
                  </TableCell>
                  <TableCell label="Upload Date">
                    <Typography fontSize={14} noWrap>{formattedDate || "-"}</Typography>
                  </TableCell>
                  <TableCell label="Uploaded By">
                    <Typography fontSize={14} noWrap>{item?.adminName || "-"}</Typography>
                  </TableCell>
                  <TableCell label="Status" flex={2}>
                    <Typography fontSize={14} fontWeight={700} noWrap sx={{ color: statusColor(item?.status) }}>
                      {item?.status}
                    </Typography>
                    {(message || value) && (
                      <Typography variant="caption" color={greyEnabled} noWrap>
                        {message} {value}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell label="Actions" isLast lastColumnWidth={90}>
                    <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }}>
                      <Tooltip title="Download file">
                        <IconButton
                          onClick={() => downloadExcel(item?.id)}
                          sx={{ bgcolor: greenLight, "&:hover": { bgcolor: "#D6EEE0" } }}
                        >
                          <DownloadRounded sx={{ color: "#1E6446", fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </Box>
        )}
      </CardContent>
      {showList && (
        <CardActions sx={{ px: 4, pb: 3 }}>
          <Box width="100%">
            <TableFooterPagination
              count={count}
              page={page - 1}
              onPageChange={handleChangePage}
              summaryText={customText}
              rowsPerPage={ROWS_PER_PAGE}
            />
          </Box>
        </CardActions>
      )}
    </Box>
  );
}
